import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import { SketchPicker } from "react-color";
import { useIntl } from "react-intl";
import CloseIcon from "@mui/icons-material/Close";
import FormatColorFillOutlinedIcon from "@mui/icons-material/FormatColorFillOutlined";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormLabel,
  IconButton,
  MenuItem,
  Popover,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { ACTIONS } from "./helpers/actions";
import ORDER_STATUSES from "./helpers/orderCategories";
import OrderActionSelector from "./OrderActionSelector";

/* === DEFAULT DATA === */

/* const ACTION_TRIGGER_LABELS = {
  IMMEDIATLY: intl.formatMessage({ id: "incomingOrders.status.triggers.onStatusChanged" }),
  WHEN_ALL_ARE_THE_SAME: intl.formatMessage({ id: "incomingOrders.status.triggers.whenAllRowsMatch" })
} */

const getActionTriggerLabels = (intl) => ({
  IMMEDIATLY: intl.formatMessage({
    id: "incomingOrders.status.triggers.onStatusChanged",
  }),
  WHEN_ALL_ARE_THE_SAME: intl.formatMessage({
    id: "incomingOrders.status.triggers.whenAllRowsMatch",
  }),
});

// Helper to map labels to menu items
const mapToMenuItems = (obj) => Object.entries(obj).map(([value, label]) => ({ value, label }));

/* Monday-inspirerade färger (egna toner, lite urvattnade) */
const PRESET_COLORS = [
  "#A7D8C9", // soft green
  "#C9E4A7", // lime pastel
  "#FFE0A3", // soft yellow
  "#FFB3A7", // coral
  "#E3B6F3", // lavender
  "#9ED6F0", // light blue
  "#B0BEC5", // grey blue
  "#F3C7A7", // peach
];

const normalizeName = (s) => (s || "").trim().toLowerCase();

/* === COLOR HELPERS === */

//const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

//const isHex = (s) => /^#?[0-9a-fA-F]{6}$/.test((s || "").trim());

const normalizeHex = (hex) => {
  const v = (hex || "").trim();
  if (!v) return "";
  return v.startsWith("#") ? v.toUpperCase() : `#${v.toUpperCase()}`;
};



const EXECUTION_MODES = {
  IMMEDIATELY: "IMMEDIATELY",
  CONFIRM_BEFORE_EXECUTION: "CONFIRM_BEFORE_EXECUTION",
};

/* const getExecutionModeLabels = (intl) => ({
  IMMEDIATELY: intl.formatMessage({
    id: "incomingOrders.status.runImmediately",
  }),
  CONFIRM_BEFORE_EXECUTION: intl.formatMessage({
    id: "incomingOrders.status.askBeforeRun",
  }),
}); */

/* === COMPONENT === */

const OrderStatusPanel = ({
  open,
  mode,
  initialValue,
  existingNames,
  onClose,
  onSave,
}) => {
  const intl = useIntl();

  const processCategories =
    typeof PROCESS_CATEGORIES === "function"
      ? ORDER_STATUSES(intl)
      : ORDER_STATUSES;

  const availableActions =
    typeof ACTIONS === "function"
      ? ACTIONS(intl)
      : ACTIONS;

  const selectableActions =
      Object.entries(availableActions).map(
          ([value, meta]) => ({
            value,
            label: meta.labelId ? intl.formatMessage({ id: meta.labelId }) : meta.label,
          })
      );

  const DEFAULT_SUPPLIER_STATUSES =
    processCategories.map((c) => ({
      value: c.key,
      label: c.labelId ? intl.formatMessage({ id: c.labelId }) : c.label,
      color: c.color,
    }));
  
  const ACTION_TRIGGER_LABELS =
    getActionTriggerLabels(intl);

  const DEFAULT_ACTION_TRIGGERS =
    mapToMenuItems(ACTION_TRIGGER_LABELS);

/*   const EXECUTION_MODE_LABELS =
    getExecutionModeLabels(intl); */

  const [name, setName] = useState("");
  const [color, setColor] = useState(PRESET_COLORS[0]);
  const [orderStatus, setOrderStatus] = useState("");
  const [supplierStatus, setSupplierStatus] = useState("");
  const [actions, setActions] = useState([]);
  const [actionTrigger, setActionTrigger] = useState(DEFAULT_ACTION_TRIGGERS[0].value);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [touched, setTouched] = useState(false);

  const [userMustConfirmAction, setUserMustConfirmAction] = useState(false);

  /*const [executionMode, setExecutionMode] = useState(
    EXECUTION_MODES.IMMEDIATELY
  );*/

  // Popover anchor
  const [colorAnchor, setColorAnchor] = useState(null);

  useEffect(() => {
    if (!open) return;

    setTouched(false);

    const initName = initialValue?.name || "";
    const initColor = initialValue?.color || PRESET_COLORS[0];
    const initOrderStatus = initialValue?.orderStatus || "";
    const initActions = initialValue?.actions || [];
    const initSupplierStatus = initialValue?.productionStatus || DEFAULT_SUPPLIER_STATUSES?.[0]?.value || "";
    const initActionTrigger = initialValue?.actionTrigger || DEFAULT_ACTION_TRIGGERS[0].value;
    const initUserMustConfirmAction = initialValue?.userMustConfirmAction ?? false;

    setName(initName);
    setColor(initColor);
    setOrderStatus(initOrderStatus);
    setSupplierStatus(initSupplierStatus);
    setActions(selectableActions.filter(n => n.value !== "NONE" && initActions.includes(n.value)));
    setActionTrigger(initActionTrigger);
    setUserMustConfirmAction(initUserMustConfirmAction);

    const adv = Boolean(
      initOrderStatus ||
      (initActions && initActions.length > 0)
    );
    setShowAdvanced(adv);

    /*const initExecutionMode =
      initialValue?.actionExecutionMode ||
      EXECUTION_MODES.IMMEDIATELY;

    setExecutionMode(initExecutionMode);*/

  }, [open/*, initialValue*/]);

  const nameError = useMemo(() => {
    const n = normalizeName(name);
    if (!touched) return "";
    if (!n) {
      return intl.formatMessage({
        id: "incomingOrders.status.nameRequired",
      });
    }
    const exists = existingNames
      .filter((x) => normalizeName(x) !== normalizeName(initialValue?.name || ""))
      .some((x) => normalizeName(x) === n);
    if (exists) {
      return intl.formatMessage({
        id: "incomingOrders.status.nameAlreadyExists",
      });
    }
    if (name.trim().length < 2) return intl.formatMessage({ id: "incomingOrders.status.nameTooShort" });
    return "";
  }, [name, existingNames, touched, initialValue]);

  const canSave = !nameError && normalizeName(name);

  const handleSave = () => {
    setTouched(true);
    if (!canSave) return;

    onSave({
      id: initialValue?.id || undefined,
      name: name.trim(),
      color: normalizeHex(color),
      //orderStatus: orderStatus || "",
      productionStatus:
        supplierStatus ||
        DEFAULT_SUPPLIER_STATUSES?.[1]?.value ||
        DEFAULT_SUPPLIER_STATUSES?.[0]?.value ||
        "",
      actions: (actions || []).map(n => n.value),
      actionTrigger: actionTrigger || DEFAULT_ACTION_TRIGGERS[0].value,
      userMustConfirmAction: userMustConfirmAction,
    });
  };

  //Reset om action is removed
  useEffect(() => {
    if (actions.length === 0 || actions[0].value === "NONE") {
      setUserMustConfirmAction(false);
    }
  }, [actions]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        {mode === "edit" ? intl.formatMessage({ id: "incomingOrders.status.editStatus" }) : intl.formatMessage({ id: "incomingOrders.status.createCustomStatus" })}
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 10, top: 10 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          {/* STATUSNAMN + FÄRG */}
          <Box>
            <Stack direction="row" spacing={1.25} alignItems="flex-start">
              <TextField
                fullWidth
                label={intl.formatMessage({ id: "incomingOrders.status.name" })}
                value={name}
                onChange={(e) => setName(e.target.value)}
                onBlur={() => setTouched(true)}
                placeholder={intl.formatMessage({ id: "incomingOrders.status.nameExample" })}
                error={Boolean(nameError)}
                helperText={nameError || intl.formatMessage({ id: "incomingOrders.status.displayName" })}
              />

              {/* FÄRGRUTA */}
              <Tooltip title={intl.formatMessage({ id: "incomingOrders.status.selectColor" })} arrow>
                <Box
                  onClick={(e) => setColorAnchor(e.currentTarget)}
                  sx={{
                    width: 50,
                    height: 50,
                    borderRadius: "10px",
                    backgroundColor: color,
                    cursor: "pointer",
                    border: "1px solid rgba(0,0,0,0.18)",
                    display: "grid",
                    placeItems: "center",
                    boxShadow:
                      "inset 0 0 0 1px rgba(255,255,255,0.28), 0 1px 2px rgba(0,0,0,0.06)",
                    transition: "transform 120ms ease, box-shadow 120ms ease",
                    "&:hover": {
                      transform: "translateY(-1px)",
                      boxShadow:
                        "inset 0 0 0 1px rgba(255,255,255,0.30), 0 6px 16px rgba(0,0,0,0.10)",
                    },
                  }}
                >
                  <FormatColorFillOutlinedIcon sx={{ color: "rgba(0,0,0,0.55)", fontSize: 18 }} />
                </Box>
              </Tooltip>
            </Stack>

            {/* PRESET COLORS */}
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {intl.formatMessage({
                  id: "incomingOrders.status.quickSelect",
                })}
              </Typography>

              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {PRESET_COLORS.map((c) => {
                  const selected = normalizeHex(c) === normalizeHex(color);
                  return (
                    <Box
                      key={c}
                      onClick={() => setColor(c)}
                      sx={{
                        width: 22,
                        height: 22,
                        borderRadius: "7px",
                        backgroundColor: c,
                        cursor: "pointer",
                        border: selected
                          ? "1px solid rgba(0,0,0,0.35)"
                          : "1px solid rgba(0,0,0,0.14)",
                        boxShadow: selected
                          ? "0 0 0 2px rgba(0,0,0,0.28)"
                          : "none",
                        transition: "transform 120ms ease",
                        "&:hover": { transform: "scale(1.08)" },
                      }}
                    />
                  );
                })}
              </Box>
            </Stack>
          </Box>

          <TextField
            select
            label={intl.formatMessage({
              id: "incomingOrders.status.processCategory",
            })}
            fullWidth
            value={supplierStatus}
            helperText={intl.formatMessage({ id: "incomingOrders.status.processCategoryHelp" })}
            onChange={(e) => setSupplierStatus(e.target.value)}
          >
            {DEFAULT_SUPPLIER_STATUSES.map((s) => (
              <MenuItem key={s.value} value={s.value}>
                {s.label}
              </MenuItem>
            ))}
          </TextField>
          <Divider />

          {/* ADVANCED */}
          <Box>
            <Button
              onClick={() => setShowAdvanced((s) => !s)}
              startIcon={showAdvanced ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              sx={{ textTransform: "none", px: 0 }}
            >
              {intl.formatMessage({
                id: "incomingOrders.status.automaticEventsOptional",
              })}
            </Button>

            <Collapse in={showAdvanced} timeout={220}>
              <Stack spacing={2} sx={{ mt: 1.5 }}>
                <Typography variant="body2" color="text.secondary">
                  {intl.formatMessage({ id: "incomingOrders.status.whatShouldHappenWhenSet" })}
                </Typography>

                <OrderActionSelector
                  label={intl.formatMessage({ id: "incomingOrders.status.automaticAction" })}
                  selectableActions={selectableActions}
                  onChange={newSelection => setActions(newSelection)}
                  selection={actions}
                />

                <TextField
                  select
                  label={intl.formatMessage({ id: "incomingOrders.status.whenShouldActionRun" })}
                  fullWidth
                  value={actionTrigger}
                  helperText={intl.formatMessage({ id: "incomingOrders.status.actionTimingHelp" })}
                  onChange={(e) => setActionTrigger(e.target.value)}
                >
                  {DEFAULT_ACTION_TRIGGERS.map((s) => (
                    <MenuItem key={s.value} value={s.value}>
                      {s.label}
                    </MenuItem>
                  ))}
                </TextField>

                {(actions.length > 0 && actions[0].value !== "NONE") && (
                  <FormControl sx={{ mt: 1 }}>
                    <FormLabel
                      component="legend"
                      sx={{
                        typography: "body2",
                        color: "text.secondary",
                        mb: 1,
                      }}
                    >
                      {intl.formatMessage({
                        id: "incomingOrders.status.confirmation",
                      })}
                    </FormLabel>

                    <ToggleButtonGroup
                      exclusive
                      size="small"
                      value={userMustConfirmAction}
                      onChange={(e, value) => {
                        if (value !== null) {
                          setUserMustConfirmAction(value);
                        }
                      }}
                      sx={{
                        "& .MuiToggleButton-root": {
                          textTransform: "none",
                          px: 2,
                        },
                      }}
                    >
                      <ToggleButton value={false}>
                        {intl.formatMessage({
                          id: "incomingOrders.status.runImmediately",
                        })}
                      </ToggleButton>

                      <ToggleButton value={true}>
                        {intl.formatMessage({
                          id: "incomingOrders.status.askBeforeAction",
                        })}
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </FormControl>
                )}
              </Stack>
            </Collapse>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>
          {intl.formatMessage({
            id: "btn.cancel",
          })}
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={!canSave}>
          {mode === "edit" ? intl.formatMessage({ id: "incomingOrders.status.saveChanges" }) : intl.formatMessage({ id: "incomingOrders.status.saveStatus" })}
        </Button>
      </DialogActions>

      {/* === COLOR POPOVER (snabbval + HEX/RGB/HSL) === */}
      <Popover
        open={Boolean(colorAnchor)}
        anchorEl={colorAnchor}
        onClose={() => setColorAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: { sx: { p: 1.5, borderRadius: 2, overflow: "hidden" } },
        }}
      >
        <SketchPicker
          color={color}
          onChange={(c) => setColor(c.hex)}
          presetColors={PRESET_COLORS}
          disableAlpha
        />
      </Popover>
    </Dialog>
  );
};

OrderStatusPanel.propTypes = {
  open: PropTypes.bool.isRequired,
  mode: PropTypes.oneOf(["create", "edit"]),
  initialValue: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    color: PropTypes.string,
    orderStatus: PropTypes.string,
    supplierStatus: PropTypes.string,
    actions: PropTypes.arrayOf(PropTypes.string),
    actionTrigger: PropTypes.string,
    actionExecutionMode: PropTypes.oneOf([
      EXECUTION_MODES.IMMEDIATELY,
      EXECUTION_MODES.CONFIRM_BEFORE_EXECUTION,
    ]),
  }),
  existingNames: PropTypes.arrayOf(PropTypes.string),
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

OrderStatusPanel.defaultProps = {
  mode: "create",
  initialValue: {},
  existingNames: [],
};

export default OrderStatusPanel;
